/* global Modernizr */
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Color, GradientRadioInput, RadioGroup } from '@figure/frontlib-gui';
import { userAgentUtil } from '@figure/frontlib-util';

const highlightOption = css`
  background-color: ${Color.PURPLE_RAIN};

  label span {
    color: ${Color.WHITE};
  }
`;

/* Several CSS overrides included here, this component will be rebuilt in the new component library */
const OptionContainer = styled(RadioGroup)`
  margin-bottom: 16px;
  margin-top: 0;

  && legend {
    color: ${Color.DARK_GREY_2};
    font-size: 1.3rem;
    font-weight: 400;
    letter-spacing: 0.025em; /* .325 px */
    line-height: 1.64; /* 21.32px */
  }

  div {
    margin-top: 0;
  }

  div > div {
    &:hover {
      ${highlightOption}
    }
  }

  div > div:first-of-type {
    border-radius: 4px 4px 0px 0px;
  }

  div > div:last-of-type {
    border-radius: 0px 0px 4px 4px;
  }

  input + span {
    display: none;
  }

  .gradient-radio-input-container {
    padding: 0;
  }

  label {
    cursor: pointer;
    padding: 20px;
    width: 100%;
  }
`;

const Option = styled.span`
  display: block;
  color: ${({ checked }) => (checked ? `${Color.WHITE}` : `${Color.BLACK_SOUL}`)};
  font-size: 1.5rem;
  font-weight: 400;
  letter-spacing: 0.0213em; /* .32px */
  line-height: 1.54; /* 23.1px */
  margin: 0;
`;

const OptionDescription = styled.span`
  color: ${({ checked }) => (checked ? `${Color.WHITE}` : `${Color.DARK_GREY_2}`)};
  font-size: 1.5rem;
  font-weight: 400;
  letter-spacing: 0.0213em; /* .32px */
  line-height: 1.54; /* 23.1px */
  margin: 0;
`;

const USER_MEDIA_CHECK = Modernizr.getusermedia;
const showTakeAPicture = !userAgentUtil.isMobileDevice() || USER_MEDIA_CHECK;

const IDUploadOptions = ({ handleClick, uploadOption, showBypass, showEmail, idBypass }) => (
  <>
    <OptionContainer label="Select an option" className="option-container">
      <GradientRadioInput
        data-test-id="webcam"
        name="IDVerification"
        id="Upload-file"
        label={
          <>
            {/* space added after option to improve screen reader users experience */}
            <Option checked={uploadOption === 'UPLOAD'}>Upload file</Option>{' '}
            <OptionDescription checked={uploadOption === 'UPLOAD'}>
              Provide an image (jpeg/png) of your ID.
            </OptionDescription>
          </>
        }
        value="upload"
        onChange={() => handleClick('UPLOAD')}
        checked={uploadOption === 'UPLOAD'}
        fsMask={false}
      />
      {showTakeAPicture && (
        <GradientRadioInput
          name="IDVerification"
          id="webcam"
          label={
            <>
              {/* space added after option to improve screen reader users experience */}
              <Option checked={uploadOption === 'WEBCAM'}>Take a picture</Option>{' '}
              <OptionDescription checked={uploadOption === 'WEBCAM'}>
                Use your device camera to take a picture of your ID.
              </OptionDescription>
            </>
          }
          value="webcam"
          onChange={() => handleClick('WEBCAM')}
          checked={uploadOption === 'WEBCAM'}
          fsMask={false}
        />
      )}
      {showEmail && (
        <GradientRadioInput
          name="IDVerification"
          id="email"
          label={
            <>
              {/* space added after option to improve screen reader users experience */}
              <Option checked={uploadOption === 'EMAIL'}>Provide via email</Option>{' '}
              <OptionDescription checked={uploadOption === 'EMAIL'}>
                We will email you a link for you to respond and share your ID.
              </OptionDescription>
            </>
          }
          value="email"
          onChange={() => handleClick('EMAIL')}
          checked={uploadOption === 'EMAIL'}
          aria-describedby="email-option"
          fsMask={false}
        />
      )}
      {showBypass && !idBypass && (
        <GradientRadioInput
          name="IDVerification"
          id="bypass"
          label={
            <>
              {/* space added after option to improve screen reader users experience */}
              <Option checked={uploadOption === 'BYPASS'}>Remind me later</Option>{' '}
              <OptionDescription checked={uploadOption === 'BYPASS'}>
                Return to this screen later to provide your ID, which is required.
              </OptionDescription>
            </>
          }
          value="bypass"
          onChange={() => handleClick('BYPASS')}
          checked={uploadOption === 'BYPASS'}
          fsMask={false}
        />
      )}
    </OptionContainer>
  </>
);

IDUploadOptions.propTypes = {
  handleClick: PropTypes.func.isRequired,
  uploadOption: PropTypes.oneOf(['UPLOAD', 'WEBCAM', 'EMAIL', 'BYPASS', '']).isRequired,
  showBypass: PropTypes.bool,
  showEmail: PropTypes.bool,
  idBypass: PropTypes.bool,
};

IDUploadOptions.defaultProps = {
  showBypass: true,
  showEmail: true,
  idBypass: false,
};

export default IDUploadOptions;
